import React, { useMemo, useRef, useState, useEffect } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PageTitle from "../components/PageTitle";
import { useQuery } from "@tanstack/react-query";
import { useCart } from "../context/CartContext";
import { useCategories } from "../utils/useCategories";
import { useAuth } from "../context/AuthContext";
import { toggleFavoriteProduct } from "../utils/wishlistApi";
import CategoriesSlider from "../components/CategoriesSlider";
import { useLanguage } from "../context/LanguageContext";
import { getProductLink } from "../utils/productLink";
import { normalizeSearchQuery } from "../utils/searchApi";

function ShopCollectionPage() {
  const { addItem, open, items: cartItems } = useCart();
  const { categories } = useCategories();
  const { token } = useAuth();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeFilter, setActiveFilter] = useState(null);

  // Debug render counter
  const renderCount = useRef(0);
  renderCount.current += 1;
  console.log("🔄 ShopCollection render #", renderCount.current);

  // Extract parameters from URL
  const searchQuery = searchParams.get("search") || "";
  const selectedCategoryId = searchParams.get("category_id")
    ? parseInt(searchParams.get("category_id"))
    : null;
  const selectedBrandId = searchParams.get("brand_id")
    ? parseInt(searchParams.get("brand_id"))
    : null;
  const selectedSubCategId = searchParams.get("subcateg_id")
    ? parseInt(searchParams.get("subcateg_id"))
    : null;
  const selectedTypeId = searchParams.get("type_id")
    ? parseInt(searchParams.get("type_id"))
    : null;
  const page = parseInt(searchParams.get("page")) || 0;
  const limit = parseInt(searchParams.get("limit")) || 20;

  // Extract attribute filter parameters (like mobile app)
  const colorId = searchParams.get("color_id");
  const designId = searchParams.get("design_id");
  const rankId = searchParams.get("rank_id");
  const sizeId = searchParams.get("size_id");
  const vendorId = searchParams.get("vendor_id");
  const [isLoading, setIsLoading] = useState(false);

  // Debug URL parameter extraction
  console.log("🔗 URL params extracted:", {
    searchQuery,
    selectedCategoryId,
    selectedBrandId,
    selectedSubCategId,
    selectedTypeId,
    page,
    limit,
    colorId,
    designId,
    rankId,
    sizeId,
    vendorId,
    searchParamsString: searchParams.toString(),
  });

  // Monitor URL changes
  useEffect(() => {
    console.log(
      "🔗 URL changed - searchParams updated:",
      searchParams.toString()
    );
    console.log("🔗 Current window.location.href:", window.location.href);
  }, [searchParams]);

  // Scroll to top when page opens or URL parameters change
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, [searchParams]);

  // Sync filters with URL parameters
  useEffect(() => {
    setFilters({
      availability: null,
      brand: vendorId || null,
      color: colorId || null,
      size: sizeId || null,
      rank: rankId || null,
      design: designId || null,
    });
  }, [colorId, designId, rankId, sizeId, vendorId]);

  const toggleFilter = (filterId) => {
    setActiveFilter(activeFilter === filterId ? null : filterId);
  };

  const [layout, setLayout] = useState("grid-4");
  const [sortValue, setSortValue] = useState("Sorting");
  // Initialize filters from URL parameters
  const [filters, setFilters] = useState(() => ({
    availability: null,
    brand: vendorId || null,
    color: colorId || null,
    size: sizeId || null,
    rank: rankId || null,
    design: designId || null,
  }));

  // Attribute fetching states (like mobile app)
  const [showColorFetchLayer, setShowColorFetchLayer] = useState(true);
  const [showSizeFetchLayer, setShowSizeFetchLayer] = useState(true);
  const [showRankFetchLayer, setShowRankFetchLayer] = useState(true);
  const [showDesignFetchLayer, setShowDesignFetchLayer] = useState(true);

  const [showColorFetchLayerLoading, setShowColorFetchLayerLoading] =
    useState(false);
  const [showSizeFetchLayerLoading, setShowSizeFetchLayerLoading] =
    useState(false);
  const [showRankFetchLayerLoading, setShowRankFetchLayerLoading] =
    useState(false);
  const [showDesignFetchLayerLoading, setShowDesignFetchLayerLoading] =
    useState(false);

  // Fetched attribute data
  const [fetchedColors, setFetchedColors] = useState([]);
  const [fetchedSizes, setFetchedSizes] = useState([]);
  const [fetchedRanks, setFetchedRanks] = useState([]);
  const [fetchedDesigns, setFetchedDesigns] = useState([]);
  const removeFilter = (key) => {
    setFilters((prev) => ({ ...prev, [key]: null }));

    // Map filter types to API parameter names (like mobile app)
    const filterParamMap = {
      color: "color_id",
      design: "design_id",
      rank: "rank_id",
      size: "size_id",
      brand: "vendor_id",
      availability: "availability",
    };

    const apiParam = filterParamMap[key];
    if (apiParam) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete(apiParam);
      setSearchParams(newParams);
    }
  };
  const removeAllFilters = () => {
    setFilters({
      availability: null,
      brand: null,
      color: null,
      size: null,
      rank: null,
      design: null,
    });

    // Clear all filter parameters from URL
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("color_id");
    newParams.delete("design_id");
    newParams.delete("rank_id");
    newParams.delete("size_id");
    newParams.delete("vendor_id");
    newParams.delete("availability");
    setSearchParams(newParams);
  };

  // Reset attribute fetch layers (like mobile app)
  const resetShowAttributeFetchLayer = () => {
    setShowColorFetchLayer(true);
    setShowSizeFetchLayer(true);
    setShowRankFetchLayer(true);
    setShowDesignFetchLayer(true);
  };

  // Reset filters (like mobile app)
  const resetFilters = () => {
    setFilters({
      availability: null,
      brand: null,
      color: null,
      size: null,
      rank: null,
      design: null,
    });
  };

  // API call for products with filters
  const queryKey = [
    "productsGrouped",
    selectedCategoryId,
    selectedBrandId,
    selectedSubCategId,
    selectedTypeId,
    searchQuery,
    page,
    limit,
    colorId,
    designId,
    rankId,
    sizeId,
    vendorId,
    language, // Include language in queryKey to refetch when language changes
    searchParams.toString(), // Include full search params string
  ];

  console.log("🔑 useQuery key:", queryKey);

  const {
    data: productsResponse,
    isLoading: isLoadingProducts,
    isError: isErrorProducts,
  } = useQuery({
    queryKey,
    enabled: !!(
      selectedCategoryId ||
      selectedBrandId ||
      selectedSubCategId ||
      selectedTypeId ||
      searchQuery
    ),
    queryFn: async () => {
      setIsLoading(true);

      let url = "https://erp-elsalab.odoo.com/api/cs/products-grouped-brand?";
      const params = new URLSearchParams();

      if (searchQuery) {
        params.append("search_query", normalizeSearchQuery(searchQuery));
        params.append("groupby", "search");
      }

      // When subcategory is selected, use category_id with subcateg_id value
      // Otherwise, use the original category_id
      if (selectedSubCategId) {
        params.append("category_id", selectedSubCategId);
      } else if (selectedCategoryId) {
        params.append("category_id", selectedCategoryId);
      }

      if (selectedBrandId) {
        params.append("vendor_id", selectedBrandId);
      }

      if (selectedTypeId) {
        params.append("type_id", selectedTypeId);
      }

      // Add attribute filter parameters (like mobile app)
      if (colorId) {
        params.append("color_id", colorId);
      }
      if (designId) {
        params.append("design_id", designId);
      }
      if (rankId) {
        params.append("rank_id", rankId);
      }
      if (sizeId) {
        params.append("size_id", sizeId);
      }
      if (vendorId) {
        params.append("vendor_id", vendorId);
      }

      params.append("page", page);
      params.append("limit", limit);
      // Always include lang parameter, default to "en" if language is not set
      params.append("lang", language || "en");

      url += params.toString();

      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "text/plain; charset=UTF-8",
        },
        body: "",
      });

      if (!res.ok) {
        throw new Error(`Products fetch failed with status ${res.status}`);
      }

      const buffer = await res.arrayBuffer();
      const text = new TextDecoder("utf-8").decode(buffer);
      const data = JSON.parse(text);

      if (data?.data && Array.isArray(data.data) && data.data.length > 0) {
        const group = data.data[0] || {};
        const items = Array.isArray(group.products) ? group.products : [];

        const products = items.map((p) => ({
          id: String(p.id || p.template_id || ""),
          name: p.name || "",
          title: p.name || "",
          price: typeof p.price === "number" ? p.price : null,
          list_price: typeof p.list_price === "number" ? p.list_price : null,
          image:
            p.image_url_1920 ||
            p.image_url ||
            (p.id ? `https://erp-elsalab.odoo.com/web/image/${p.id}` : ""),
          image_url_1920: p.image_url_1920 || null,
          image_url: p.image_url || null,
          availability:
            p.stock && p.stock[0] && p.stock[0].total_free > 0
              ? "In stock"
              : "Out of stock",
          brand: p.att_model_name || "",
          colors: p.att_color ? [p.att_color] : [],
          sizes: p.att_size && p.att_size !== "-" ? [p.att_size] : [],
          att_size: p.att_size && p.att_size !== "-" ? p.att_size : null,
          att_specifications: p.att_specifications || null,
          description: p.description || null,
          categ_name: p.categ_name || null,
          current_offer: p.current_offer || null,
          att_color: p.att_color || null,
          package_quantity: p.package_quantity || 1,
          stock: p.stock || [],
          category: p.category || null,
          is_favorite: p.is_favorite || false,
        }));

        return {
          products,
          total: data.count || 0,
          hasMore: items.length >= limit,
          // Only include brands when no brand is selected (category selection)
          brands: !selectedBrandId ? group.brands || [] : [],
          subcategs: group.subcategs || [],
          types: group.types || [],
        };
      }

      return {
        products: [],
        total: 0,
        hasMore: false,
        // Only include brands when no brand is selected (category selection)
        brands: !selectedBrandId ? [] : [],
        subcategs: [],
        types: [],
      };
    },
    staleTime: 0,
    cacheTime: 0,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: false,
    retry: false,
    onError: (error) => {
      console.error("❌ Products API error:", error);
      setIsLoading(false);
    },
  });

  // Stop loading overlay when API fails
  useEffect(() => {
    if (isErrorProducts) {
      console.log("❌ Products API failed, stopping loading overlay");
      setIsLoading(false);
    }
  }, [isErrorProducts]);

  // Stop loading overlay when products are successfully loaded
  useEffect(() => {
    if (productsResponse && !isLoadingProducts && !isErrorProducts) {
      console.log("✅ Products loaded successfully, stopping loading overlay");
      setIsLoading(false);
    }
  }, [productsResponse, isLoadingProducts, isErrorProducts]);

  // Fallback timeout to prevent stuck loading state
  useEffect(() => {
    if (isLoading) {
      const timeout = setTimeout(() => {
        console.log(
          "⏰ Loading timeout reached, forcing loading state to false"
        );
        setIsLoading(false);
      }, 10000); // 10 second timeout

      return () => clearTimeout(timeout);
    }
  }, [isLoading]);

  // Update brands, subcategs, and types when data changes
  const [loadedBrands, setLoadedBrands] = useState([]);
  const [loadedSubCategs, setLoadedSubCategs] = useState([]);
  const [loadedTypes, setLoadedTypes] = useState([]);

  useEffect(() => {
    if (productsResponse) {
      console.log("📦 Updating loaded data:", {
        brands: productsResponse.brands?.length || 0,
        subcategs: productsResponse.subcategs?.length || 0,
        types: productsResponse.types?.length || 0,
        selectedCategoryId,
        selectedBrandId,
        selectedSubCategId,
        selectedTypeId,
      });

      // Update brands only when category changes (not when brand/subcateg/type changes)
      if (
        productsResponse.brands &&
        productsResponse.brands.length > 0 &&
        !selectedBrandId
      ) {
        console.log("📦 Updating brands (category change)");
        setLoadedBrands(productsResponse.brands);
      }

      // Update subcategs when category or brand changes
      if (
        productsResponse.subcategs &&
        productsResponse.subcategs.length > 0 &&
        !selectedSubCategId
      ) {
        console.log("📦 Updating subcategs");
        setLoadedSubCategs(productsResponse.subcategs);
      }

      // Update types when category, brand, or subcateg changes
      if (
        productsResponse.types &&
        productsResponse.types.length > 0 &&
        !selectedTypeId
      ) {
        console.log("📦 Updating types");
        setLoadedTypes(productsResponse.types);
      }

      // Loading state is now handled by the dedicated useEffect above
    }
  }, [
    productsResponse,
    selectedCategoryId,
    selectedBrandId,
    selectedSubCategId,
    selectedTypeId,
  ]);

  // Handler functions for selections - using React Router navigation
  const handleCategorySelect = (categoryId) => {
    console.log("🏷️ Category selected:", categoryId);
    setIsLoading(true);

    // Close any open filter dropdowns
    setActiveFilter(null);

    // Reset attribute fetch layers (like mobile app)
    resetShowAttributeFetchLayer();
    resetFilters();

    const newParams = new URLSearchParams();
    newParams.set("category_id", categoryId);
    newParams.set("page", "0");
    newParams.set("limit", "20");

    // Remove all filter parameters
    newParams.delete("color_id");
    newParams.delete("design_id");
    newParams.delete("rank_id");
    newParams.delete("size_id");

    // Clear all dependent data immediately to prevent stale state
    setLoadedBrands([]);
    setLoadedSubCategs([]);
    setLoadedTypes([]);

    console.log("🚀 Updating search params:", newParams.toString());
    setSearchParams(newParams);
  };

  const handleBrandSelect = (brandId) => {
    console.log("🏷️ Brand selected:", brandId);
    console.log("🔍 Current searchParams:", searchParams.toString());
    console.log("🔍 Current selectedBrandId:", selectedBrandId);
    console.log("🔍 Current URL before navigation:", window.location.href);
    console.log("🔍 isLoading state:", isLoading);

    if (isLoading) {
      console.log("⏳ Brand selection ignored - already loading");
      return;
    }

    setIsLoading(true);

    // Close any open filter dropdowns
    setActiveFilter(null);

    // Reset attribute fetch layers (like mobile app)
    resetShowAttributeFetchLayer();
    resetFilters();

    const newParams = new URLSearchParams(searchParams);
    newParams.set("brand_id", brandId);
    newParams.delete("subcateg_id");
    newParams.delete("type_id");
    newParams.set("page", "0");
    newParams.set("limit", "20");

    // Remove all filter parameters
    newParams.delete("color_id");
    newParams.delete("design_id");
    newParams.delete("rank_id");
    newParams.delete("size_id");

    console.log("🔍 New params:", newParams.toString());

    // Clear dependent data immediately to prevent stale state
    setLoadedSubCategs([]);
    setLoadedTypes([]);

    const newUrl = `/shop/collection?${newParams.toString()}`;
    console.log("🚀 Navigating to:", newUrl);

    try {
      setSearchParams(newParams);
      console.log("✅ setSearchParams called successfully");

      // Check if URL actually changed after a brief delay
      setTimeout(() => {
        console.log("🔍 URL after setSearchParams:", window.location.href);
        console.log("🔍 New searchParams:", searchParams.toString());
      }, 100);
    } catch (error) {
      console.error("❌ setSearchParams error:", error);
    }
  };

  const handleSubCategSelect = (subCategId) => {
    console.log("🏷️ Subcategory selected:", subCategId);
    console.log("🔍 Current searchParams:", searchParams.toString());
    console.log("🔍 isLoading state:", isLoading);

    if (isLoading) {
      console.log("⏳ Subcategory selection ignored - already loading");
      return;
    }

    setIsLoading(true);

    // Close any open filter dropdowns
    setActiveFilter(null);

    // Reset attribute fetch layers (like mobile app)
    resetShowAttributeFetchLayer();
    resetFilters();

    const newParams = new URLSearchParams(searchParams);
    newParams.set("subcateg_id", subCategId);
    newParams.delete("type_id");
    newParams.set("page", "0");
    newParams.set("limit", "20");

    // Remove all filter parameters
    newParams.delete("color_id");
    newParams.delete("design_id");
    newParams.delete("rank_id");
    newParams.delete("size_id");

    // Clear dependent data immediately to prevent stale state
    setLoadedTypes([]);

    console.log("🚀 Updating search params:", newParams.toString());
    setSearchParams(newParams);
  };

  const handleTypeSelect = (typeId) => {
    console.log("🏷️ Type selected:", typeId);
    console.log("🔍 Current searchParams:", searchParams.toString());
    console.log("🔍 isLoading state:", isLoading);

    if (isLoading) {
      console.log("⏳ Type selection ignored - already loading");
      return;
    }

    setIsLoading(true);

    // Reset attribute fetch layers (like mobile app)
    resetShowAttributeFetchLayer();
    resetFilters();

    const newParams = new URLSearchParams(searchParams);
    newParams.set("type_id", typeId);
    newParams.set("page", "0");
    newParams.set("limit", "20");

    console.log("🚀 Updating search params:", newParams.toString());
    setSearchParams(newParams);
  };

  const handleSearch = (query) => {
    console.log("🔍 Search query:", query);
    setIsLoading(true);

    // Reset attribute fetch layers (like mobile app)
    resetShowAttributeFetchLayer();
    resetFilters();

    const newParams = new URLSearchParams();
    newParams.set("search", query);
    newParams.set("page", "0");
    newParams.set("limit", "20");

    // Clear all dependent data immediately to prevent stale state
    setLoadedBrands([]);
    setLoadedSubCategs([]);
    setLoadedTypes([]);

    console.log("🚀 Updating search params:", newParams.toString());
    setSearchParams(newParams);
  };

  // Handler for filter selections that update route and refetch
  const handleFilterSelect = (filterType, value) => {
    console.log(`🔍 Filter selected: ${filterType} = ${value}`);
    setIsLoading(true);

    // Reset attribute fetch layers (like mobile app)
    resetShowAttributeFetchLayer();

    // Close the dropdown after selection
    setActiveFilter(null);

    const newParams = new URLSearchParams(searchParams);

    // Map filter types to API parameter names (like mobile app)
    const filterParamMap = {
      color: "color_id",
      design: "design_id",
      rank: "rank_id",
      size: "size_id",
      brand: "vendor_id",
      availability: "availability",
    };

    const apiParam = filterParamMap[filterType];
    if (apiParam) {
      newParams.set(apiParam, value);
    }

    newParams.set("page", "0");
    newParams.set("limit", "20");

    console.log("🚀 Updating search params with filter:", newParams.toString());
    setSearchParams(newParams);
  };

  // Fetch attributes from API (like mobile app)
  const fetchAndSetAttributes = async (attributeType, filterOptions = {}) => {
    try {
      let url = "https://erp-elsalab.odoo.com/api/cs/product-attributes?";
      const params = new URLSearchParams();

      params.append("attribute", attributeType);

      // Add language parameter
      if (language === "ar") {
        params.append("lang", "ar");
      } else {
        params.append("lang", "en");
      }

      // Add current filter options
      if (filterOptions.category_id)
        params.append("category_id", filterOptions.category_id);
      if (filterOptions.vendor_id)
        params.append("vendor_id", filterOptions.vendor_id);
      if (filterOptions.subcateg_id)
        params.append("subcateg_id", filterOptions.subcateg_id);
      if (filterOptions.type_id)
        params.append("type_id", filterOptions.type_id);

      url += params.toString();

      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "text/plain; charset=UTF-8",
        },
        body: "",
      });

      if (!res.ok) {
        throw new Error(`Attributes fetch failed with status ${res.status}`);
      }

      const buffer = await res.arrayBuffer();
      const text = new TextDecoder("utf-8").decode(buffer);
      const data = JSON.parse(text);

      if (data?.data && Array.isArray(data.data) && data.data.length > 0) {
        const attributeData = data.data[0];

        // Extract the specific attribute type from the response
        const attributeValues = attributeData[`${attributeType}s`] || [];

        const attributes = attributeValues.map((attr) => ({
          id: attr.id,
          name: attr.name,
          label: attr.name,
        }));

        return attributes;
      }

      return [];
    } catch (error) {
      console.error(`Error fetching ${attributeType} attributes:`, error);
      return [];
    }
  };

  // Handle attribute fetch (like mobile app)
  const handleAttributeFetch = async (attributeType) => {
    // eslint-disable-next-line no-eval
    const setLoadingState = eval(
      `setShow${
        attributeType.charAt(0).toUpperCase() + attributeType.slice(1)
      }FetchLayerLoading`
    );
    // eslint-disable-next-line no-eval
    const setFetchLayer = eval(
      `setShow${
        attributeType.charAt(0).toUpperCase() + attributeType.slice(1)
      }FetchLayer`
    );
    // eslint-disable-next-line no-eval
    const setFetchedData = eval(
      `setFetched${
        attributeType.charAt(0).toUpperCase() + attributeType.slice(1)
      }s`
    );

    try {
      setLoadingState(true);

      const filterOptions = {
        category_id: selectedCategoryId,
        vendor_id: selectedBrandId,
        subcateg_id: selectedSubCategId,
        type_id: selectedTypeId,
      };

      const attributes = await fetchAndSetAttributes(
        attributeType,
        filterOptions
      );

      console.log(`✅ Fetched ${attributeType} attributes:`, attributes);
      setFetchedData(attributes);
      setFetchLayer(false);
      setLoadingState(false);
    } catch (error) {
      console.error(`Error fetching ${attributeType}:`, error);
      setLoadingState(false);
    }
  };

  // When language changes, refetch attribute APIs (colors, sizes, rank, design) so labels use new locale
  const prevLanguageRef = useRef(language);
  useEffect(() => {
    if (prevLanguageRef.current === language) return;
    prevLanguageRef.current = language;

    const filterOptions = {
      category_id: selectedCategoryId,
      vendor_id: selectedBrandId,
      subcateg_id: selectedSubCategId,
      type_id: selectedTypeId,
    };

    const refetchAttributesForLocale = async () => {
      try {
        const [colors, sizes, ranks, designs] = await Promise.all([
          fetchAndSetAttributes("color", filterOptions),
          fetchAndSetAttributes("size", filterOptions),
          fetchAndSetAttributes("rank", filterOptions),
          fetchAndSetAttributes("design", filterOptions),
        ]);
        setFetchedColors(colors);
        setFetchedSizes(sizes);
        setFetchedRanks(ranks);
        setFetchedDesigns(designs);
        setShowColorFetchLayer(false);
        setShowSizeFetchLayer(false);
        setShowRankFetchLayer(false);
        setShowDesignFetchLayer(false);
      } catch (e) {
        console.warn("Error refetching attributes for locale:", e);
      }
    };

    refetchAttributesForLocale();
  }, [
    language,
    selectedCategoryId,
    selectedBrandId,
    selectedSubCategId,
    selectedTypeId,
  ]);

  const [localFavoriteState, setLocalFavoriteState] = useState({});

  const products = useMemo(() => {
    // Use API data only
    if (productsResponse && productsResponse.products.length > 0) {
      return productsResponse.products.map((p) => ({
        ...p,
        is_favorite:
          localFavoriteState[p.id] !== undefined
            ? localFavoriteState[p.id]
            : p.is_favorite || false,
      }));
    }

    // No fallback data - return empty array
    return [];
  }, [productsResponse, localFavoriteState]);

  // Extract unique filter values from products
  const extractedFilterOptions = useMemo(() => {
    if (!products || products.length === 0) {
      return {
        colors: [],
        sizes: [],
        ranks: [],
        designs: [],
        brands: [],
        availability: [],
      };
    }

    const colors = [
      ...new Set(products.map((p) => p.att_color).filter(Boolean)),
    ];
    const sizes = [...new Set(products.map((p) => p.att_size).filter(Boolean))];
    const ranks = [
      ...new Set(products.map((p) => p.attribute_rank).filter(Boolean)),
    ];
    const designs = [
      ...new Set(products.map((p) => p.att_design).filter(Boolean)),
    ];
    const brands = [
      ...new Set(products.map((p) => p.att_model_name).filter(Boolean)),
    ];
    const availability = [
      ...new Set(products.map((p) => p.availability).filter(Boolean)),
    ];

    return {
      colors: colors.map((color) => ({ id: color, label: color })),
      sizes: sizes.map((size) => ({ id: size, label: size })),
      ranks: ranks.map((rank) => ({ id: rank, label: rank })),
      designs: designs.map((design) => ({ id: design, label: design })),
      brands: brands.map((brand) => ({ id: brand, label: brand })),
      availability: availability.map((avail) => ({ id: avail, label: avail })),
    };
  }, [products]);

  // Helper function to get filter label from ID
  const getFilterLabel = (filterKey, filterId) => {
    if (!filterId) return filterId;

    const idString = String(filterId);

    switch (filterKey) {
      case "color":
        const color = fetchedColors.find((c) => String(c.id) === idString);
        return color ? color.label : filterId;
      case "size":
        const size = fetchedSizes.find((s) => String(s.id) === idString);
        return size ? size.label : filterId;
      case "rank":
        const rank = fetchedRanks.find((r) => String(r.id) === idString);
        return rank ? rank.label : filterId;
      case "design":
        const design = fetchedDesigns.find((d) => String(d.id) === idString);
        return design ? design.label : filterId;
      default:
        return filterId;
    }
  };

  const appliedFilters = Object.entries(filters).filter(
    ([, v]) => v !== null && v !== undefined
  );
  // Only apply client-side sorting; filtering is done server-side via API
  const filteredProducts = useMemo(() => {
    let list = [...products];

    // Only apply sorting client-side
    switch (sortValue) {
      case "Price, low to high":
        return list.sort((a, b) => (a.price || 0) - (b.price || 0));
      case "Price, high to low":
        return list.sort((a, b) => (b.price || 0) - (a.price || 0));
      default:
        return list;
    }
  }, [products, sortValue]);

  const brandScrollRef1 = useRef(null);
  const sellerScrollRef = useRef(null);
  const brandScrollRef2 = useRef(null);
  const categoryScrollRef = useRef(null);
  const scrollByRef = (ref, delta) => {
    if (ref.current) ref.current.scrollBy({ left: delta, behavior: "smooth" });
  };

  // Ensure selected category is visible in the categories scroller
  useEffect(() => {
    try {
      if (!selectedCategoryId || !categoryScrollRef.current) return;
      const node = categoryScrollRef.current.querySelector(
        `[data-category-id="${selectedCategoryId}"]`
      );
      if (node) {
        const container = categoryScrollRef.current;
        const nodeLeft = node.offsetLeft;
        const nodeRight = nodeLeft + node.offsetWidth;
        const viewLeft = container.scrollLeft;
        const viewRight = viewLeft + container.clientWidth;
        if (nodeLeft < viewLeft || nodeRight > viewRight) {
          container.scrollTo({
            left: Math.max(nodeLeft - 24, 0),
            behavior: "smooth",
          });
        }
      }
    } catch {}
  }, [selectedCategoryId, categories]);

  const colorHex = {
    Black: "#111827",
    White: "#ffffff",
    Blue: "#2563eb",
  };

  const brands = useMemo(() => {
    console.log("🏷️ Brands useMemo - loadedBrands:", loadedBrands.length);
    // Use API data only
    if (loadedBrands.length > 0) {
      const mappedBrands = loadedBrands.map((brand) => ({
        id: brand.id,
        src:
          brand.image_url ||
          `https://erp-elsalab.odoo.com/web/image/${brand.id}`,
        alt: brand.name,
        dataBrand: brand.name,
        name: brand.name,
      }));
      console.log("🏷️ Mapped brands:", mappedBrands.length);
      return mappedBrands;
    }

    // No fallback data - return empty array
    return [];
  }, [loadedBrands]);

  const subcategs = useMemo(() => {
    // Use API data only
    if (loadedSubCategs.length > 0) {
      return loadedSubCategs.map((subcateg) => ({
        id: subcateg.id,
        imgSrc:
          subcateg.image_url ||
          `https://erp-elsalab.odoo.com/web/image/${subcateg.id}`,
        title: subcateg.name,
        alt: subcateg.name,
        name: subcateg.name,
      }));
    }

    // No fallback data - return empty array
    return [];
  }, [loadedSubCategs]);

  const types = useMemo(() => {
    // Use API data only
    if (loadedTypes.length > 0) {
      return loadedTypes.map((type) => ({
        id: type.id,
        label: type.name,
        dataType: type.id,
        name: type.name,
      }));
    }

    // No fallback data - return empty array
    return [];
  }, [loadedTypes]);

  return (
    <div>
      <style>
        {`
          /* Product image framing to improve separation and aesthetics */
          .card-product .product-img {
            display: block;
            border-radius: 12px;
            background: #ffffff;
            box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
            outline: 1px solid rgba(14, 90, 167, 0.4);
            transition: box-shadow 0.25s ease, transform 0.25s ease;
            position: relative;
          }

          .card-product .product-img img {
            border-radius: 10px;
          }

          .card-product:hover .product-img {
            transform: translateY(-2px);
            box-shadow: 0 8px 22px rgba(0, 0, 0, 0.12);
          }

          /* Ensure hover controls remain above the frame */
          .card-product .list-product-btn {
            z-index: 2;
          }

          /* Highlight selected category card with thicker darker blue border */
          .card-product.style-8.active {
            border: 3px solid #0a4a8a !important;
            box-shadow: 0 4px 12px rgba(10, 74, 138, 0.4) !important;
          }
          .card-product.style-8.active .product-img {
            outline: 3px solid #0a4a8a !important;
            box-shadow: 0 8px 22px rgba(10, 74, 138, 0.4) !important;
          }
          .card-product.style-8.active .title { color: #0a4a8a !important; }

          /* Highlight selected brand with thicker darker blue border */
          .brand-item-v2-edit.active {
            border: 3px solid #0a4a8a !important;
            box-shadow: 0 4px 12px rgba(10, 74, 138, 0.4) !important;
          }

          /* Bluish text color for categories, brands, and subcategories */
          .shop-collection-section-1 .card-product-info .title,
          .shop-collection-section-1 .card-product-info .title.link,
          .shop-collection-section-3 .card-product-info .title,
          .shop-collection-section-3 .card-product-info .title.link {
            color: #0e5aa7 !important;
            font-weight: 600 !important;
          }

          .shop-collection-section-1 .card-product.style-8.active .title,
          .shop-collection-section-3 .card-product.style-8.active .title {
            color: #0a4a8a !important;
          }

          /* Mobile: ensure wishlist shows */
          @media only screen and (max-width: 575px) {
            .card-product .list-product-btn .wishlist {
              display: inline-flex !important;
              visibility: visible !important;
              opacity: 1 !important;
            }

            /* Make prices same size as desktop view on mobile */
            .card-product .price-area .current-price,
            .card-product .price-area .price.current-price,
            .card-product .card-product-info .price-area .current-price,
            .card-product .card-product-info .price-area .price.current-price,
            .card-product .card-product-info .price-area span.price.current-price {
              font-size: 1rem !important;
              line-height: 1rem !important;
            }
            
            .card-product .price-area .compare-at-price,
            .card-product .price-area .price.compare-at-price,
            .card-product .card-product-info .price-area .compare-at-price,
            .card-product .card-product-info .price-area .price.compare-at-price,
            .card-product .card-product-info .price-area span.price.compare-at-price {
              font-size: 0.9rem !important;
              line-height: 0.9rem !important;
            }
          }

          /* Make cart (bag) and favorite (heart) icons red within product cards */
          .card-product .list-product-btn .icon,
          .card-product .list-product-btn .icon:before {
            color: var(--primary) !important;
          }
          
          /* Hover effect: only change icon color, not background */
          .card-product .list-product-btn .box-icon {
            background-color: rgba(26, 115, 232, 0.15) !important;
            transition: background-color 0.3s ease, color 0.3s ease !important;
          }
          
          .card-product .list-product-btn .box-icon:hover {
            background-color: rgba(26, 115, 232, 0.15) !important; /* Keep same background */
            color: #0e5aa7 !important; /* Only change icon color to blue */
          }
          
          .card-product .list-product-btn .box-icon:hover .icon,
          .card-product .list-product-btn .box-icon:hover .icon:before {
            color: #0e5aa7 !important; /* Change icon color on hover */
          }

          /* Cart icon bluish filled when product is in cart */
          .card-product .list-product-btn .box-icon.quick-add.product-in-cart {
            background-color: #0e5aa7 !important;
            color: #ffffff !important;
          }

          .card-product .list-product-btn .box-icon.quick-add.product-in-cart:hover {
            background-color: #0e5aa7 !important; /* Keep bluish background on hover */
            color: #ffffff !important;
          }

          .card-product .list-product-btn .box-icon.quick-add.product-in-cart .icon,
          .card-product .list-product-btn .box-icon.quick-add.product-in-cart .icon:before,
          .card-product .list-product-btn .box-icon.quick-add.product-in-cart:hover .icon,
          .card-product .list-product-btn .box-icon.quick-add.product-in-cart:hover .icon:before {
            color: #ffffff !important;
          }

          /* Add separation and elevation to product items */
          .card-product.grid,
          .card-product.list-layout,
          .card-product.style-8 {
            background: #fafafa; /* off-white */
            border: 1px solid rgba(14, 90, 167, 0.5);
            border-radius: 12px;
            box-shadow: 0 2px 8px rgba(14, 90, 167, 0.15);
            padding: 12px;
          }

          /* Ensure inner content respects rounded corners */
          .card-product .card-product-wrapper,
          .card-product .card-product-info {
            border-radius: 10px;
            overflow: hidden;
          }

          /* Smaller price text */
          .card-product .price-area .current-price {
            font-weight: 600;
            font-size: 1.1rem;
          }
          .card-product .price-area .compare-at-price {
            font-weight: 400;
            font-size: 1rem;
            opacity: 0.85;
            position: relative;
            text-decoration: none !important;
          }
          
          .card-product .price-area .compare-at-price::before {
            content: '';
            position: absolute;
            left: 0;
            top: 50%;
            width: 100%;
            height: 1px;
            background-color: #000;
            transform: rotate(-15deg);
            transform-origin: center;
          }

          /* Make category slider items same height */
          .shop-collection-wrapper-1 {
            display: flex !important;
            align-items: stretch !important;
          }

          .shop-collection-slide-1 {
            display: flex !important;
            flex-direction: column !important;
            height: 100% !important;
          }

          .shop-collection-slide-1 .card-product.style-8 {
            display: flex !important;
            flex-direction: column !important;
            height: 100% !important;
            min-height: auto !important;
          }

          .shop-collection-slide-1 .card-product-wrapper {
            flex-shrink: 0 !important;
            width: 100% !important;
          }
          
          /* Make category items wider on all screen sizes */
          .shop-collection-slide-1 {
            width: calc((100% - 48px) / 3.5) !important;
            min-width: 140px !important;
            max-width: 180px !important;
          }
          
          .shop-collection-slide-1 .card-product.style-8 {
            width: 100% !important;
            min-width: 100% !important;
          }
          
          .shop-collection-slide-1 .card-product-wrapper {
            width: 100% !important;
            min-width: 100% !important;
          }
          
          .shop-collection-slide-1 .product-img {
            width: 100% !important;
            min-width: 100% !important;
            max-width: 100% !important;
          }
          
          .shop-collection-slide-1 .product-img img {
            width: 100% !important;
            min-width: 100% !important;
            max-width: 100% !important;
            height: auto !important;
            object-fit: cover !important;
          }
          
          /* Ensure images stay wide on very small screens */
          @media (max-width: 480px) {
            .shop-collection-slide-1 {
              min-width: 140px !important;
            }
            
            .shop-collection-slide-1 .card-product.style-8 {
              width: 100% !important;
              min-width: 100% !important;
            }
            
            .shop-collection-slide-1 .card-product-wrapper {
              width: 100% !important;
              min-width: 100% !important;
            }
            
            .shop-collection-slide-1 .product-img {
              width: 100% !important;
              min-width: 100% !important;
              max-width: 100% !important;
            }
            
            .shop-collection-slide-1 .product-img img {
              width: 100% !important;
              min-width: 100% !important;
              max-width: 100% !important;
              height: auto !important;
              object-fit: cover !important;
            }
          }
          
          .shop-collection-slide-1 .card-product-info {
            flex: 0 0 auto !important;
            display: flex !important;
            flex-direction: column !important;
            justify-content: flex-start !important;
            padding-top: 4px !important;
            padding-bottom: 0px !important;
            width: 100% !important;
            padding-left: 4px !important;
            padding-right: 4px !important;
          }
          
          .shop-collection-slide-1 .card-product-info .title {
            display: -webkit-box !important;
            -webkit-line-clamp: 2 !important;
            -webkit-box-orient: vertical !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
            line-height: 1.2 !important;
            min-height: 2.4em !important;
            max-height: 2.4em !important;
            margin: 0 !important;
            font-size: 13px !important;
            width: 100% !important;
            word-wrap: break-word !important;
            word-break: break-word !important;
            overflow-wrap: break-word !important;
            white-space: normal !important;
          }

          /* Remove borders from navigation arrows */
          .shop-collection-nav-1-left,
          .shop-collection-nav-1-right,
          .shop-collection-nav-2-left,
          .shop-collection-nav-2-right,
          .shop-collection-nav-3-left,
          .shop-collection-nav-3-right {
            border: none !important;
            outline: none !important;
            box-shadow: none !important;
          }

          /* Hide slider arrows on mobile */
          @media (max-width: 767px) {
            .shop-collection-nav-1-left,
            .shop-collection-nav-1-right,
            .shop-collection-nav-2-left,
            .shop-collection-nav-2-right,
            .shop-collection-nav-3-left,
            .shop-collection-nav-3-right {
              display: none !important;
            }
            
          }
          
          .shop-collection-nav-1-left .icon,
          .shop-collection-nav-1-right .icon,
          .shop-collection-nav-2-left .icon,
          .shop-collection-nav-2-right .icon,
          .shop-collection-nav-3-left .icon,
          .shop-collection-nav-3-right .icon {
            border: none !important;
            outline: none !important;
          }
          
          /* Mobile: Show 2 products per row instead of 4 */
          @media (max-width: 768px) {
            .tf-grid-layout.tf-col-4 {
              grid-template-columns: repeat(2, 1fr) !important;
            }
            .tf-grid-layout.tf-col-3 {
              grid-template-columns: repeat(2, 1fr) !important;
            }
            .tf-grid-layout.tf-col-2 {
              grid-template-columns: repeat(2, 1fr) !important;
            }
            .tf-grid-layout.tf-col-5 {
              grid-template-columns: repeat(2, 1fr) !important;
            }
            .tf-grid-layout.tf-col-6 {
              grid-template-columns: repeat(2, 1fr) !important;
            }
          }
          
          /* Remove spacing above products control row */
          .shop-collection-section-6 .tf-shop-control {
            margin-top: 0px !important;
            padding-top: 0px !important;
            margin-bottom: 8px !important;
            padding-bottom: 0px !important;
          }
          
          .shop-collection-section-6 {
            padding-top: 0px !important;
          }
          
          /* Add spacing between control row and products */
          .shop-collection-section-6 .wrapper-control-shop {
            margin-top: 0px !important;
            padding-top: 0px !important;
          }
          
          @media (max-width: 768px) {
            .shop-collection-section-6 .tf-shop-control {
              margin-bottom: 8px !important;
            }
            
            .shop-collection-section-6 .wrapper-control-shop {
              margin-top: 0px !important;
            }
          }
          
          @media (max-width: 575px) {
            .shop-collection-section-6 .tf-shop-control {
              margin-bottom: 6px !important;
            }
            
            .shop-collection-section-6 .wrapper-control-shop {
              margin-top: 0px !important;
            }
          }
          
          /* Reduce vertical spacing between the three sliders */
          .shop-collection-section-1 {
            padding-top: 0px !important;
            padding-bottom: 0px !important;
            margin-top: 0px !important;
            margin-bottom: 10px !important;
          }
          
          .shop-collection-section-2 {
            padding-top: 0px !important;
            padding-bottom: 0px !important;
            margin-top: 10px !important;
            margin-bottom: 0px !important;
          }
          
          /* Override flat-spacing-25 default spacing */
          .shop-collection-section-1.flat-spacing-25,
          .shop-collection-section-2.flat-spacing-25 {
            padding-top: 0px !important;
            padding-bottom: 0px !important;
          }
          
          /* Remove container padding on smaller screens */
          @media (max-width: 768px) {
            .shop-collection-section-1 .container,
            .shop-collection-section-2 .container {
              padding-top: 0px !important;
              padding-bottom: 0px !important;
            }
          }
          
          @media (max-width: 575px) {
            .shop-collection-section-1 .container,
            .shop-collection-section-2 .container {
              padding-top: 0px !important;
              padding-bottom: 0px !important;
            }
          }
          
          /* Tablet spacing */
          @media (min-width: 769px) and (max-width: 1024px) {
            .shop-collection-section-1 {
              margin-bottom: 5px !important;
            }
            
            .shop-collection-section-2 {
              margin-top: 5px !important;
            }
          }
          
          /* Ensure proper spacing on desktop */
          @media (min-width: 1025px) {
            .shop-collection-section-1 {
              margin-bottom: 10px !important;
            }
            
            .shop-collection-section-2 {
              margin-top: 10px !important;
            }
          }
          
          /* Reduce spacing on smaller screens */
          @media (max-width: 768px) {
            .shop-collection-section-1 {
              margin-bottom: -15px !important;
            }
            
            .shop-collection-section-2 {
              margin-top: -15px !important;
            }
            
            .shop-collection-section-1 .container,
            .shop-collection-section-2 .container {
              padding-top: 0px !important;
              padding-bottom: 0px !important;
            }
          }
          
          /* Medium screens - prevent overlap */
          @media (min-width: 481px) and (max-width: 768px) {
            .shop-collection-section-1 {
              margin-bottom: -10px !important;
            }
            
            .shop-collection-section-2 {
              margin-top: -10px !important;
            }
          }
          
          @media (max-width: 575px) {
            .shop-collection-section-1 {
              margin-bottom: -20px !important;
            }
            
            .shop-collection-section-2 {
              margin-top: -20px !important;
            }
            
            .shop-collection-section-1 .container,
            .shop-collection-section-2 .container {
              padding-top: 0px !important;
              padding-bottom: 0px !important;
            }
          }
          
          /* Very small screens - prevent overlap */
          @media (max-width: 480px) {
            .shop-collection-section-1 {
              margin-bottom: -10px !important;
            }
            
            .shop-collection-section-2 {
              margin-top: -10px !important;
              margin-bottom: 10px !important;
            }
            
            .shop-collection-section-3 {
              margin-top: 10px !important;
            }
          }
          
          @media (max-width: 375px) {
            .shop-collection-section-1 {
              margin-bottom: 0px !important;
            }
            
            .shop-collection-section-2 {
              margin-top: 0px !important;
              margin-bottom: 15px !important;
            }
            
            .shop-collection-section-3 {
              margin-top: 15px !important;
            }
          }
          
          .shop-collection-section-3 {
            padding-top: 0px !important;
            padding-bottom: 0px !important;
            margin-top: 0px !important;
            margin-bottom: 0px !important;
          }

          /* Add spacing between subcategory items */
          .shop-collection-wrapper-3 {
            gap: 12px !important;
          }

          .shop-collection-slide-3 {
            margin-right: 0px !important;
            margin-left: 0px !important;
          }

          .shop-collection-slide-3 .card-product.style-8 {
            margin-right: 0px !important;
            margin-left: 0px !important;
          }

          .shop-collection-slider-3 .swiper-wrapper {
            gap: 12px !important;
          }
          
          /* Ensure categories slider in section-1 has proper spacing */
          .shop-collection-section-1 .shop-collection-wrapper-3 {
            gap: 12px !important;
          }
          
          .shop-collection-section-1 .shop-collection-slider-3 .swiper-wrapper {
            gap: 12px !important;
          }
          
          /* Fixed height for category items to fit two-line titles */
          .shop-collection-section-1 .shop-collection-slide-3 .card-product.style-8 {
            display: flex !important;
            flex-direction: column !important;
          }
          
          .shop-collection-section-1 .shop-collection-slide-3 .card-product-wrapper {
            flex-shrink: 0 !important;
          }
          
          .shop-collection-section-1 .shop-collection-slide-3 .card-product-info {
            flex: 0 0 auto !important;
            min-height: 38px !important;
            display: flex !important;
            flex-direction: column !important;
            justify-content: center !important;
          }
          
          /* Reduce spacing between subcategory items on mobile (only for section-3) - half of desktop */
          @media (max-width: 768px) {
            .shop-collection-section-3 .shop-collection-wrapper-3,
            .shop-collection-section-3 .shop-collection-slider-3 .swiper-wrapper {
              gap: 0px !important;
            }
            /* Use negative margins to bring items closer */
            .shop-collection-section-3 .shop-collection-slide-3 {
              margin-left: -6px !important;
              margin-right: -6px !important;
            }
            .shop-collection-section-3 .shop-collection-slide-3:first-child {
              margin-left: 0 !important;
            }
            .shop-collection-section-3 .shop-collection-slide-3:last-child {
              margin-right: 0 !important;
            }
            /* Reduce padding on subcategory items to reduce spacing */
            .shop-collection-section-3 .shop-collection-slide-3 .card-product.style-8 {
              padding: 4px !important;
            }
          }
          
          @media (max-width: 575px) {
            .shop-collection-section-3 .shop-collection-wrapper-3,
            .shop-collection-section-3 .shop-collection-slider-3 .swiper-wrapper {
              gap: 0px !important;
            }
            /* Use negative margins to bring items closer */
            .shop-collection-section-3 .shop-collection-slide-3 {
              margin-left: -6px !important;
              margin-right: -6px !important;
            }
            .shop-collection-section-3 .shop-collection-slide-3:first-child {
              margin-left: 0 !important;
            }
            .shop-collection-section-3 .shop-collection-slide-3:last-child {
              margin-right: 0 !important;
            }
            /* Reduce padding on subcategory items to reduce spacing */
            .shop-collection-section-3 .shop-collection-slide-3 .card-product.style-8 {
              padding: 4px !important;
            }
          }
          
          .shop-collection-section-2 {
            margin-top: 0px !important;
            padding-top: 0px !important;
          }
          
          .shop-collection-section-3 {
            margin-top: 0px !important;
            padding-top: 0px !important;
          }

          /* Add more spacing below subcategory slider on desktop only */
          @media (min-width: 769px) {
            .shop-collection-section-3 {
              margin-bottom: 15px !important;
              padding-bottom: 5px !important;
            }
          }
          
          /* Further reduce spacing on mobile */
          @media (max-width: 768px) {
            .shop-collection-section-1,
            .shop-collection-section-2,
            .shop-collection-section-3 {
              padding-top: 0px !important;
              padding-bottom: 0px !important;
            }
            
            .shop-collection-section-1 {
              margin-bottom: -15px !important;
            }
            
            .shop-collection-section-2 {
              margin-top: -15px !important;
              margin-bottom: -10px !important;
              padding-top: 0px !important;
              padding-bottom: 0px !important;
            }
            
            .shop-collection-section-2 .container {
              padding-top: 0px !important;
              padding-bottom: 0px !important;
            }
            
            .shop-collection-section-2 .wrap-carousel {
              margin-top: 0px !important;
              margin-bottom: 0px !important;
              padding-top: 0px !important;
              padding-bottom: 0px !important;
            }
            
            .shop-collection-section-3 {
              margin-top: 0px !important;
              padding-top: 0px !important;
            }
          }
          
          @media (max-width: 575px) {
            .shop-collection-section-1,
            .shop-collection-section-2,
            .shop-collection-section-3 {
              padding-top: 0px !important;
              padding-bottom: 0px !important;
            }
            
            .shop-collection-section-1 {
              margin-bottom: -20px !important;
            }
            
            .shop-collection-section-2 {
              margin-top: -20px !important;
              margin-bottom: -10px !important;
              padding-top: 0px !important;
              padding-bottom: 0px !important;
            }
            
            .shop-collection-section-2 .container {
              padding-top: 0px !important;
              padding-bottom: 0px !important;
            }
            
            .shop-collection-section-2 .wrap-carousel {
              margin-top: 0px !important;
              margin-bottom: 0px !important;
              padding-top: 0px !important;
              padding-bottom: 0px !important;
            }
            
            .shop-collection-section-3 {
              margin-top: -10px !important;
              padding-top: 0px !important;
            }
          }
          
          /* Reduce spacing between third slider and filter section */
          .shop-collection-section-5 {
            padding-top: 5px !important;
            margin-top: -30px !important;
          }
          
          @media (max-width: 768px) {
            .shop-collection-section-5 {
              padding-top: 3px !important;
              margin-top: -35px !important;
            }
          }
          
          @media (max-width: 575px) {
            .shop-collection-section-5 {
              padding-top: 0px !important;
              margin-top: -40px !important;
            }
          }
          
          /* Center brand and subcategories sliders when there are few items */
          .shop-collection-wrapper-2,
          .shop-collection-wrapper-3 {
            justify-content: flex-start;
          }
          
          /* When content doesn't overflow, center it */
          .shop-collection-wrapper-2:not(:has(+ *)) {
            justify-content: center;
          }
          
          /* Use a more reliable approach with a wrapper check */
          .shop-collection-slider-2,
          .shop-collection-slider-3 {
            display: flex;
            justify-content: center;
          }
          
          .shop-collection-wrapper-2[data-few-items="true"],
          .shop-collection-wrapper-3[data-few-items="true"] {
            justify-content: center !important;
          }

          /* Remove left/right whitespace on all screen sizes - make sliders full width */
          /* Ensure page wrapper doesn't add padding */
          body > div:first-child {
            padding-left: 0 !important;
            padding-right: 0 !important;
          }

          /* Remove section padding and margins */
          .shop-collection-section-1,
          .shop-collection-section-2,
          .shop-collection-section-3 {
            padding-left: 0 !important;
            padding-right: 0 !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
            overflow: hidden !important;
            box-sizing: border-box !important;
          }

          /* Remove container padding for slider sections */
          .shop-collection-section-1 .container,
          .shop-collection-section-2 .container,
          .shop-collection-section-3 .container,
          .shop-collection-section-1 .shop-collection-section-container,
          .shop-collection-section-2 .shop-collection-section-container,
          .shop-collection-section-3 .shop-collection-section-container {
            padding-left: 0 !important;
            padding-right: 0 !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
            max-width: 100% !important;
            width: 100% !important;
            box-sizing: border-box !important;
          }

          /* Remove padding from wrapper elements */
          .shop-collection-section-1 .wrap-carousel,
          .shop-collection-section-2 .wrap-carousel,
          .shop-collection-section-3 .wrap-carousel {
            padding-left: 0 !important;
            padding-right: 0 !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
            width: 100% !important;
            box-sizing: border-box !important;
          }

          /* Make slider containers full width */
          .shop-collection-section-1 .shop-collection-slider-3,
          .shop-collection-section-2 .shop-collection-slider-2,
          .shop-collection-section-3 .shop-collection-slider-3 {
            padding-left: 0 !important;
            padding-right: 0 !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
            width: 100% !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
          }

          /* Remove padding from slider wrappers (overrides inline styles) */
          .shop-collection-section-1 .shop-collection-wrapper-3,
          .shop-collection-section-2 .shop-collection-wrapper-2,
          .shop-collection-section-3 .shop-collection-wrapper-3,
          .shop-collection-section-1 .swiper-wrapper,
          .shop-collection-section-2 .swiper-wrapper,
          .shop-collection-section-3 .swiper-wrapper,
          .shop-collection-section-1 .swiper-wrapper.no-scrollbar,
          .shop-collection-section-2 .swiper-wrapper.no-scrollbar,
          .shop-collection-section-3 .swiper-wrapper.no-scrollbar {
            padding-left: 0 !important;
            padding-right: 0 !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
            box-sizing: border-box !important;
          }

          /* Ensure swiper elements are full width */
          .shop-collection-section-1 .swiper,
          .shop-collection-section-2 .swiper,
          .shop-collection-section-3 .swiper,
          .shop-collection-section-1 .tf-sw-product-sell-1,
          .shop-collection-section-2 .tf-sw-brand,
          .shop-collection-section-3 .tf-sw-product-sell-1 {
            padding-left: 0 !important;
            padding-right: 0 !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
            width: 100% !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
          }

          /* Bluish color for pagination/page switcher */
          .tf-pagination-list .pagination-link {
            color: #0e5aa7 !important;
            border-color: rgba(14, 90, 167, 0.3) !important;
          }

          .tf-pagination-list .pagination-link:hover {
            background-color: rgba(14, 90, 167, 0.1) !important;
            border-color: #0e5aa7 !important;
            color: #0e5aa7 !important;
          }

          .tf-pagination-list .active .pagination-link {
            background-color: #0e5aa7 !important;
            color: #ffffff !important;
            border-color: #0e5aa7 !important;
            box-shadow: 0 2px 8px rgba(14, 90, 167, 0.3) !important;
          }

          .tf-pagination-list .disabled .pagination-link {
            color: rgba(14, 90, 167, 0.3) !important;
            border-color: rgba(14, 90, 167, 0.2) !important;
            cursor: not-allowed !important;
          }
        `}
      </style>
      <Header />
      {/* <PageTitle title="Search" /> */}

      {/* Loading Overlay */}
      {(isLoading || isLoadingProducts) && !isErrorProducts && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "20px 30px",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              gap: "12px",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            }}
          >
            <div
              className="spinner-border text-primary"
              role="status"
              style={{ width: "20px", height: "20px" }}
            >
              <span className="visually-hidden">Loading...</span>
            </div>
            <span style={{ fontSize: "16px", fontWeight: "500" }}>
              {language === "ar" ? "جاري إعادة التحميل..." : "Refetching..."}
            </span>
          </div>
        </div>
      )}

      {/* Categories Section - Section 1 */}
      {categories && categories.length > 0 && (
        <CategoriesSlider
          items={categories}
          selectedId={selectedCategoryId}
          onItemSelect={handleCategorySelect}
          isLoading={isLoading}
          sectionClass="shop-collection-section-1"
          sliderClass="shop-collection-slider-3"
          wrapperClass="shop-collection-wrapper-3"
          slideClass="shop-collection-slide-3"
          navLeftClass="shop-collection-nav-3-left"
          navRightClass="shop-collection-nav-3-right"
        />
      )}

      {/* Brand Section - Section 2 - Only show when category is selected */}
      {selectedCategoryId && (
        <section className="flat-spacing-25 shop-collection-section-2">
          <div className="container shop-collection-section-container">
            <div className="wrap-carousel wrap-brand wrap-brand-v2 autoplay-linear">
              <div
                className="swiper tf-sw-brand border-0 no-scrollbar shop-collection-slider-2"
                style={{ position: "relative", overflow: "visible" }}
              >
                <div
                  className="swiper-wrapper no-scrollbar shop-collection-wrapper-2"
                  ref={brandScrollRef1}
                  data-few-items={brands.length <= 5 ? "true" : "false"}
                  style={{
                    display: "flex",
                    overflowX: "auto",
                    scrollSnapType: "x mandatory",
                    gap: "20px",
                    cursor: "grab",
                    padding: "10px 0",
                    scrollPadding: "0 20px",
                    WebkitOverflowScrolling: "touch",
                    justifyContent:
                      brands.length <= 5 ? "center" : "flex-start",
                  }}
                >
                  {brands.map((b, i) => (
                    <div
                      key={`brand-${b.name}-${i}`}
                      className="swiper-slide Brand-Section1"
                      style={{
                        flex: "0 0 auto",
                        width: "120px",
                        height: "100px",
                        scrollSnapAlign: "start",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <div
                        className={`brand-item-v2-edit${
                          selectedBrandId === b.id ? " active" : ""
                        }`}
                        {...(b.dataBrand ? { "data-brand": b.dataBrand } : {})}
                        onClick={() => {
                          console.log("🖱️ Brand clicked:", b.id, b.name);
                          console.log("🖱️ isLoading:", isLoading);
                          console.log("🖱️ Current URL:", window.location.href);
                          if (!isLoading) {
                            console.log("🚀 Calling handleBrandSelect");
                            handleBrandSelect(b.id);
                          } else {
                            console.log("⏳ Brand click ignored - loading");
                          }
                        }}
                        style={{
                          cursor: "pointer",
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: "10px",
                          backgroundColor: "#fff",
                          borderRadius: "8px",
                          border: "1px solid rgba(14, 90, 167, 0.6)",
                          transition: "all 0.3s ease",
                        }}
                        onMouseEnter={(e) => {
                          if (!e.currentTarget.classList.contains("active")) {
                            e.currentTarget.style.borderColor = "#0e5aa7";
                            e.currentTarget.style.boxShadow =
                              "0 2px 8px rgba(14, 90, 167, 0.3)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!e.currentTarget.classList.contains("active")) {
                            e.currentTarget.style.borderColor =
                              "rgba(14, 90, 167, 0.6)";
                            e.currentTarget.style.boxShadow = "none";
                          }
                        }}
                      >
                        <img
                          className="lazyload"
                          data-src={b.src}
                          src={b.src}
                          alt={b.alt}
                          style={{
                            width: "90%",
                            height: "90%",
                            objectFit: "contain",
                            objectPosition: "center",
                            transform: "scale(1)",
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                {loadedBrands.length > 0 && (
                  <>
                    <div
                      className="nav-sw nav-next-slider shop-collection-nav-2-left"
                      onClick={() => scrollByRef(brandScrollRef1, -300)}
                      style={{
                        position: "absolute",
                        left: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        zIndex: 10,
                        cursor: "pointer",
                        backgroundColor: "#ffffff",
                        border: "none",
                        outline: "none",
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                        borderRadius: "50%",
                        width: "44px",
                        height: "44px",
                        transition: "all 0.3s ease",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#0e5aa7";
                        e.currentTarget.style.boxShadow =
                          "0 6px 16px rgba(14, 90, 167, 0.3)";
                        const icon = e.currentTarget.querySelector(".icon");
                        if (icon) icon.style.color = "#ffffff";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "#ffffff";
                        e.currentTarget.style.boxShadow =
                          "0 4px 12px rgba(0, 0, 0, 0.15)";
                        const icon = e.currentTarget.querySelector(".icon");
                        if (icon) icon.style.color = "#0e5aa7";
                      }}
                    >
                      <span
                        className="icon icon-arrow-left"
                        style={{
                          fontSize: "20px",
                          color: "#0e5aa7",
                          fontWeight: "bold",
                        }}
                      ></span>
                    </div>
                    <div
                      className="nav-sw nav-prev-slider shop-collection-nav-2-right"
                      onClick={() => scrollByRef(brandScrollRef1, 300)}
                      style={{
                        position: "absolute",
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        zIndex: 10,
                        cursor: "pointer",
                        backgroundColor: "#ffffff",
                        border: "none",
                        outline: "none",
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                        borderRadius: "50%",
                        width: "44px",
                        height: "44px",
                        transition: "all 0.3s ease",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#0e5aa7";
                        e.currentTarget.style.boxShadow =
                          "0 6px 16px rgba(14, 90, 167, 0.3)";
                        const icon = e.currentTarget.querySelector(".icon");
                        if (icon) icon.style.color = "#ffffff";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "#ffffff";
                        e.currentTarget.style.boxShadow =
                          "0 4px 12px rgba(0, 0, 0, 0.15)";
                        const icon = e.currentTarget.querySelector(".icon");
                        if (icon) icon.style.color = "#0e5aa7";
                      }}
                    >
                      <span
                        className="icon icon-arrow-right"
                        style={{
                          fontSize: "20px",
                          color: "#0e5aa7",
                          fontWeight: "bold",
                        }}
                      ></span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </section>
      )}
      {/* /Brand */}

      {/* Seller Section - Section 3 - Only show when category is selected */}
      {selectedCategoryId && (
        <section className="flat-spacing-25 shop-collection-section-3">
          <div className="container shop-collection-section-container">
            <div className="wrap-carousel">
              <div className="swiper tf-sw-product-sell-1 shop-collection-slider-3">
                <div
                  className="swiper-wrapper no-scrollbar shop-collection-wrapper-3"
                  ref={sellerScrollRef}
                  data-few-items={subcategs.length <= 5 ? "true" : "false"}
                  style={{
                    display: "flex",
                    overflowX: "auto",
                    scrollSnapType: "x mandatory",
                    gap: "12px",
                    cursor: "grab",
                    padding: "0 16px",
                    WebkitOverflowScrolling: "touch",
                    justifyContent:
                      subcategs.length <= 5 ? "center" : "flex-start",
                  }}
                  // {...makeDragHandlers(sellerScrollRef, dragStateSeller)} // Temporarily disabled for debugging
                >
                  {subcategs.map((s, i) => (
                    <div
                      key={`subc-${s.title}-${i}`}
                      className="swiper-slide shop-collection-slide-3"
                      lazy="true"
                      style={{
                        flex: "0 0 auto",
                        width: "calc((100% - 72px) / 7)",
                        minWidth: "100px",
                        maxWidth: "140px",
                        scrollSnapAlign: "start",
                      }}
                    >
                      <div
                        className={`card-product style-8 Seller-Section${
                          selectedSubCategId === s.id ? " active" : ""
                        }`}
                      >
                        <div className="card-product-wrapper">
                          <div
                            className="product-img"
                            onClick={() => {
                              console.log(
                                "🖱️ Subcategory clicked:",
                                s.id,
                                s.name
                              );
                              console.log("🖱️ isLoading:", isLoading);
                              console.log(
                                "🖱️ Current URL:",
                                window.location.href
                              );
                              if (!isLoading) {
                                console.log("🚀 Calling handleSubCategSelect");
                                handleSubCategSelect(s.id);
                              } else {
                                console.log(
                                  "⏳ Subcategory click ignored - loading"
                                );
                              }
                            }}
                            style={{ cursor: "pointer" }}
                          >
                            <img
                              className="lazyload img-product"
                              data-src={s.imgSrc}
                              src={s.imgSrc}
                              alt={s.alt}
                            />
                            <img
                              className="lazyload img-hover"
                              data-src={s.imgSrc}
                              src={s.imgSrc}
                              alt={s.alt}
                            />
                          </div>
                        </div>
                        <div className="card-product-info text-center">
                          <div
                            className="title link"
                            onClick={() => {
                              console.log(
                                "🖱️ Subcategory title clicked:",
                                s.id,
                                s.name
                              );
                              console.log("🖱️ isLoading:", isLoading);
                              console.log(
                                "🖱️ Current URL:",
                                window.location.href
                              );
                              if (!isLoading) {
                                console.log("🚀 Calling handleSubCategSelect");
                                handleSubCategSelect(s.id);
                              } else {
                                console.log(
                                  "⏳ Subcategory title click ignored - loading"
                                );
                              }
                            }}
                            style={{ cursor: "pointer" }}
                          >
                            {s.title}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Navigation */}
              {loadedSubCategs.length > 0 && (
                <>
                  <div
                    className="nav-sw disable-line nav-next-slider shop-collection-nav-3-left"
                    onClick={() => scrollByRef(sellerScrollRef, -360)}
                    style={{
                      position: "absolute",
                      left: "10px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      zIndex: 10,
                      cursor: "pointer",
                      backgroundColor: "#ffffff",
                      border: "none",
                      outline: "none",
                      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                      borderRadius: "50%",
                      width: "44px",
                      height: "44px",
                      transition: "all 0.3s ease",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#0e5aa7";
                      e.currentTarget.style.boxShadow =
                        "0 6px 16px rgba(14, 90, 167, 0.3)";
                      const icon = e.currentTarget.querySelector(".icon");
                      if (icon) icon.style.color = "#ffffff";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "#ffffff";
                      e.currentTarget.style.boxShadow =
                        "0 4px 12px rgba(0, 0, 0, 0.15)";
                      const icon = e.currentTarget.querySelector(".icon");
                      if (icon) icon.style.color = "#0e5aa7";
                    }}
                  >
                    <span
                      className="icon icon-arrow-left"
                      style={{
                        fontSize: "20px",
                        color: "#0e5aa7",
                        fontWeight: "bold",
                      }}
                    ></span>
                  </div>
                  <div
                    className="nav-sw disable-line nav-prev-slider shop-collection-nav-3-right"
                    onClick={() => scrollByRef(sellerScrollRef, 360)}
                    style={{
                      position: "absolute",
                      right: "10px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      zIndex: 10,
                      cursor: "pointer",
                      backgroundColor: "#ffffff",
                      border: "none",
                      outline: "none",
                      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                      borderRadius: "50%",
                      width: "44px",
                      height: "44px",
                      transition: "all 0.3s ease",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#0e5aa7";
                      e.currentTarget.style.boxShadow =
                        "0 6px 16px rgba(14, 90, 167, 0.3)";
                      const icon = e.currentTarget.querySelector(".icon");
                      if (icon) icon.style.color = "#ffffff";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "#ffffff";
                      e.currentTarget.style.boxShadow =
                        "0 4px 12px rgba(0, 0, 0, 0.15)";
                      const icon = e.currentTarget.querySelector(".icon");
                      if (icon) icon.style.color = "#0e5aa7";
                    }}
                  >
                    <span
                      className="icon icon-arrow-right"
                      style={{
                        fontSize: "20px",
                        color: "#0e5aa7",
                        fontWeight: "bold",
                      }}
                    ></span>
                  </div>
                </>
              )}
              <div className="sw-dots style-2 sw-pagination-sell-1 justify-content-center"></div>
            </div>
          </div>
        </section>
      )}
      {/* /Seller */}

      {/* Types Section - Section 4 - Only show when category is selected */}
      {/* {selectedCategoryId && (
        <section
          className="flat-spacing-25 shop-collection-section-4"
        >
          <div className="container">
            <div className="wrap-carousel wrap-brand wrap-brand-v2 autoplay-linear">
              <div
                className="swiper tf-sw-brand border-0 no-scrollbar shop-collection-slider-4"
                style={{
                  paddingLeft: 32,
                  paddingRight: 32,
                  overflow: 'visible',
                }}
              >
                <div
                  className="swiper-wrapper no-scrollbar shop-collection-wrapper-4"
                  ref={brandScrollRef2}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflowX: 'auto',
                    scrollSnapType: 'x mandatory',
                    gap: '8px',
                    padding: '0 60px',
                    scrollPadding: '0 60px',
                    cursor: 'grab',
                    marginLeft: '20px',
                    marginRight: '20px',
                  }}
                  // {...makeDragHandlers(brandScrollRef2, dragState2)} // Temporarily disabled for debugging
                >
                  {types.map((t, i) => (
                    <div
                      key={`type-${t.name}-${i}`}
                      className="swiper-slide shop-collection-slide-4"
                      style={{
                        flex: '0 0 auto',
                        width: 'auto',
                        scrollSnapAlign: 'start',
                      }}
                    >
                      <div
                        className="brand-item-v2-edit"
                        {...(t.dataType ? { 'data-type': t.dataType } : {})}
                        onClick={() => {
                          console.log('🖱️ Type clicked:', t.id, t.name);
                          console.log('🖱️ isLoading:', isLoading);
                          console.log('🖱️ Current URL:', window.location.href);
                          if (!isLoading) {
                            console.log('🚀 Calling handleTypeSelect');
                            handleTypeSelect(t.id);
                          } else {
                            console.log('⏳ Type click ignored - loading');
                          }
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        <span
                          className="type-box"
                          style={{ whiteSpace: 'nowrap', padding: '10px 16px' }}
                        >
                          {t.label}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                <div
                  className="nav-sw nav-next-slider shop-collection-nav-4-left box-icon w_46 round"
                  onClick={() => scrollByRef(brandScrollRef2, -300)}
                  style={{
                    position: 'absolute',
                    left: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 10,
                    cursor: 'pointer',
                  }}
                >
                  <span className="icon icon-arrow-left"></span>
                </div>
                <div
                  className="nav-sw nav-prev-slider shop-collection-nav-4-right box-icon w_46 round"
                  onClick={() => scrollByRef(brandScrollRef2, 300)}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 10,
                    cursor: 'pointer',
                  }}
                >
                  <span className="icon icon-arrow-right"></span>
                </div>
              </div>
            </div>
          </div>
        </section>
      )} */}

      {/* Filter Section - Section 5 - Only show when category is selected */}
      {selectedCategoryId && (
        <section
          className="flat-spacing-2 shop-collection-section-5"
          style={{ paddingTop: "10px", paddingBottom: "10px" }}
        >
          <style>
            {`
              .filter-buttons-container {
                display: flex;
                gap: 12px;
                justify-content: center;
                flex-wrap: wrap;
                padding: 15px 0;
              }
              .filter-button {
                background-color: #0e5aa7;
                color: white;
                border: none;
                padding: 12px 24px;
                border-radius: 4px;
                font-size: 14px;
                font-weight: 600;
                cursor: pointer;
                transition: all 0.3s ease;
                min-width: 120px;
                text-align: center;
                display: flex;
                align-items: center;
                justify-content: center;
              }
              .filter-button:hover {
                background-color: #0a4a8a;
                transform: translateY(-2px);
                box-shadow: 0 4px 8px rgba(14, 90, 167, 0.3);
              }
              .filter-button.active {
                background-color: #0a4a8a;
                box-shadow: 0 2px 4px rgba(14, 90, 167, 0.4);
              }
              .filter-button .icon {
                display: inline-block;
                margin-left: 8px;
              }
              @media (max-width: 768px) {
                .filter-buttons-container {
                  gap: 8px;
                  padding: 10px 0;
                }
                .filter-button {
                  padding: 10px 16px;
                  font-size: 12px;
                  min-width: 80px;
                  flex: 1 1 calc(50% - 4px);
                }
              }
              /* Brand Section Arrows - Outside slider */
              @media (min-width: 992px) {
                .tf-sw-brand .nav-sw {
                  position: absolute;
                  top: 50%;
                  transform: translateY(-50%);
                }
                .tf-sw-brand .nav-next-slider {
                  left: -50px;
                }
                .tf-sw-brand .nav-prev-slider {
                  right: -50px;
                }
              }
              @media (max-width: 991px) {
                .tf-sw-brand .nav-sw {
                  position: absolute;
                  top: 50%;
                  transform: translateY(-50%);
                }
                .tf-sw-brand .nav-next-slider {
                  left: 5px;
                }
                .tf-sw-brand .nav-prev-slider {
                  right: 5px;
                }
              }

              .filter-buttons-container {
                display: grid;
                grid-template-columns: repeat(4, 1fr);
                gap: clamp(8px, 2vw, 16px);
                padding: clamp(10px, 2vh, 20px) clamp(10px, 2vw, 20px);
                max-width: 95%;
                margin: 0 auto;
              }
              .filter-button {
                background-color: #0e5aa7;
                color: white;
                border: none;
                padding: clamp(10px, 1.5vh, 16px) clamp(12px, 2vw, 24px);
                border-radius: clamp(4px, 0.5vw, 8px);
                font-size: clamp(11px, 1.8vw, 15px);
                font-weight: 600;
                cursor: pointer;
                transition: all 0.3s ease;
                text-align: center;
                display: flex;
                align-items: center;
                justify-content: center;
                white-space: nowrap;
                min-height: clamp(36px, 5vh, 48px);
              }
              .filter-button:hover {
                background-color: #0a4a8a;
                transform: translateY(-2px);
                box-shadow: 0 4px 8px rgba(14, 90, 167, 0.3);
              }
              .filter-button.active {
                background-color: #0a4a8a;
                box-shadow: 0 2px 4px rgba(14, 90, 167, 0.4);
              }
              .filter-button .icon {
                display: inline-block;
                margin-left: clamp(4px, 0.8vw, 8px);
                font-size: clamp(10px, 1.5vw, 14px);
              }
              @media (max-width: 768px) {
                .filter-buttons-container {
                  max-width: 95%;
                }
              }
              @media (max-width: 480px) {
                .filter-buttons-container {
                  max-width: 98%;
                  gap: clamp(6px, 1.5vw, 10px);
                }
                .filter-button {
                  font-size: clamp(9px, 2.5vw, 12px);
                }
              }

              
            `}
          </style>
          <div className="container">
            <div className="filter-buttons-container">
              {/* Filter Button: Colors */}
              <button
                className={`filter-button ${
                  activeFilter === "filterColor" ? "active" : ""
                }`}
                onClick={() => {
                  if (activeFilter === "filterColor") {
                    setActiveFilter(null);
                  } else {
                    setActiveFilter("filterColor");
                    // Always refetch when opening so locale/filters are up to date
                    handleAttributeFetch("color");
                  }
                }}
              >
                {language === "ar" ? "الالوان" : "COLORS"}
                <span
                  className="icon icon-arrow-down"
                  style={{
                    marginLeft: "8px",
                    transition: "transform 0.3s ease",
                    transform:
                      activeFilter === "filterColor"
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                  }}
                ></span>
              </button>

              {/* Filter Button: Sizes */}
              <button
                className={`filter-button ${
                  activeFilter === "filterSize" ? "active" : ""
                }`}
                onClick={() => {
                  if (activeFilter === "filterSize") {
                    setActiveFilter(null);
                  } else {
                    setActiveFilter("filterSize");
                    // Always refetch when opening so locale/filters are up to date
                    handleAttributeFetch("size");
                  }
                }}
              >
                {language === "ar" ? "المقاسات" : "SIZES"}
                <span
                  className="icon icon-arrow-down"
                  style={{
                    marginLeft: "8px",
                    transition: "transform 0.3s ease",
                    transform:
                      activeFilter === "filterSize"
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                  }}
                ></span>
              </button>

              {/* Filter Button: Rank */}
              <button
                className={`filter-button ${
                  activeFilter === "filterRank" ? "active" : ""
                }`}
                onClick={() => {
                  if (activeFilter === "filterRank") {
                    setActiveFilter(null);
                  } else {
                    setActiveFilter("filterRank");
                    // Always refetch when opening so locale/filters are up to date
                    handleAttributeFetch("rank");
                  }
                }}
              >
                {language === "ar" ? "التصنيفات" : "RANK"}
                <span
                  className="icon icon-arrow-down"
                  style={{
                    marginLeft: "8px",
                    transition: "transform 0.3s ease",
                    transform:
                      activeFilter === "filterRank"
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                  }}
                ></span>
              </button>

              {/* Filter Button: Design */}
              <button
                className={`filter-button ${
                  activeFilter === "filterDesign" ? "active" : ""
                }`}
                onClick={() => {
                  if (activeFilter === "filterDesign") {
                    setActiveFilter(null);
                  } else {
                    setActiveFilter("filterDesign");
                    // Always refetch when opening so locale/filters are up to date
                    handleAttributeFetch("design");
                  }
                }}
              >
                {language === "ar" ? "التصميمات" : "DESIGN"}
                <span
                  className="icon icon-arrow-down"
                  style={{
                    marginLeft: "8px",
                    transition: "transform 0.3s ease",
                    transform:
                      activeFilter === "filterDesign"
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                  }}
                ></span>
              </button>
            </div>

            {/* Filter Dropdowns - Show when button is active */}
            {activeFilter === "filterColor" && (
              <div
                style={{
                  marginTop: "15px",
                  padding: "15px",
                  backgroundColor: "#f9f9f9",
                  borderRadius: "8px",
                }}
              >
                {showColorFetchLayerLoading ? (
                  <div style={{ textAlign: "center", padding: "20px" }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "10px",
                      }}
                    >
                      <div
                        className="spinner-border spinner-border-sm"
                        role="status"
                        style={{ width: "16px", height: "16px" }}
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <span>
                        {language === "ar"
                          ? "جاري تحميل الألوان..."
                          : "Loading Colors..."}
                      </span>
                    </div>
                  </div>
                ) : (
                  <ul
                    className="tf-filter-group filter-color d-flex gap-3 flex-wrap"
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(80px, 1fr))",
                      gap: "8px",
                    }}
                  >
                    {fetchedColors.map((c) => {
                      const selected = filters.color === String(c.id);
                      return (
                        <li
                          key={`color-${c.id}`}
                          className="d-flex align-items-center"
                        >
                          <input
                            type="radio"
                            name="color"
                            id={c.id}
                            checked={selected}
                            onChange={() =>
                              handleFilterSelect("color", String(c.id))
                            }
                            style={{ display: "none" }}
                          />
                          <label
                            htmlFor={c.id}
                            className="d-inline-flex align-items-center"
                            style={{
                              border: selected
                                ? "2px solid #0e5aa7"
                                : "1px solid #e5e7eb",
                              padding: "6px 12px",
                              borderRadius: "20px",
                              cursor: "pointer",
                              background: selected ? "#0e5aa7" : "#fff",
                              color: selected ? "#fff" : "#000",
                              gap: 6,
                              fontSize: "12px",
                              minHeight: "32px",
                              width: "100%",
                              justifyContent: "center",
                            }}
                          >
                            <span
                              style={{
                                width: 14,
                                height: 14,
                                borderRadius: "50%",
                                border: "1px solid #e5e7eb",
                                backgroundColor: colorHex[c.label] || "#ddd",
                              }}
                            ></span>
                            <span>{c.label}</span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}

            {activeFilter === "filterSize" && (
              <div
                style={{
                  marginTop: "15px",
                  padding: "15px",
                  backgroundColor: "#f9f9f9",
                  borderRadius: "8px",
                }}
              >
                {showSizeFetchLayerLoading ? (
                  <div style={{ textAlign: "center", padding: "20px" }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "10px",
                      }}
                    >
                      <div
                        className="spinner-border spinner-border-sm"
                        role="status"
                        style={{ width: "16px", height: "16px" }}
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <span>
                        {language === "ar"
                          ? "جاري تحميل المقاسات..."
                          : "Loading Sizes..."}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      maxHeight: "160px", // 4 rows × 40px (32px height + 8px gap)
                      overflowY: "auto",
                      overflowX: "hidden",
                      WebkitOverflowScrolling: "touch",
                    }}
                  >
                    <ul
                      className="tf-filter-group d-flex gap-3 flex-wrap"
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "8px",
                        listStyle: "none",
                        padding: 0,
                        margin: 0,
                      }}
                    >
                      {fetchedSizes.map((s) => {
                        const selected = filters.size === String(s.id);
                        return (
                          <li key={`size-${s.id}`} style={{ flexShrink: 0 }}>
                            <input
                              type="radio"
                              name="size"
                              id={s.id}
                              checked={selected}
                              onChange={() =>
                                handleFilterSelect("size", String(s.id))
                              }
                              style={{ display: "none" }}
                            />
                            <label
                              htmlFor={s.id}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                padding: "6px 12px",
                                borderRadius: "20px",
                                border: selected
                                  ? "2px solid #0e5aa7"
                                  : "1px solid #e5e7eb",
                                background: selected ? "#0e5aa7" : "#fff",
                                color: selected ? "#fff" : "#000",
                                fontWeight: 600,
                                cursor: "pointer",
                                fontSize: "12px",
                                minHeight: "32px",
                                minWidth: "70px",
                                justifyContent: "center",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {s.label}
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {activeFilter === "filterRank" && (
              <div
                style={{
                  marginTop: "15px",
                  padding: "15px",
                  backgroundColor: "#f9f9f9",
                  borderRadius: "8px",
                }}
              >
                {showRankFetchLayerLoading ? (
                  <div style={{ textAlign: "center", padding: "20px" }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "10px",
                      }}
                    >
                      <div
                        className="spinner-border spinner-border-sm"
                        role="status"
                        style={{ width: "16px", height: "16px" }}
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <span>
                        {language === "ar"
                          ? "جاري تحميل التصنيفات..."
                          : "Loading Ranks..."}
                      </span>
                    </div>
                  </div>
                ) : (
                  <ul
                    className="tf-filter-group d-flex gap-3 flex-wrap"
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(70px, 1fr))",
                      gap: "8px",
                    }}
                  >
                    {fetchedRanks.map((r) => {
                      const selected = filters.rank === String(r.id);
                      return (
                        <li key={`rank-filter-${r.id}`}>
                          <input
                            type="radio"
                            name="rank"
                            id={r.id}
                            checked={selected}
                            onChange={() =>
                              handleFilterSelect("rank", String(r.id))
                            }
                            style={{ display: "none" }}
                          />
                          <label
                            htmlFor={r.id}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              padding: "6px 12px",
                              borderRadius: "20px",
                              border: selected
                                ? "2px solid #0e5aa7"
                                : "1px solid #e5e7eb",
                              background: selected ? "#0e5aa7" : "#fff",
                              color: selected ? "#fff" : "#000",
                              fontWeight: 600,
                              cursor: "pointer",
                              fontSize: "12px",
                              minHeight: "32px",
                              width: "100%",
                              justifyContent: "center",
                            }}
                          >
                            {r.label}
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}

            {activeFilter === "filterDesign" && (
              <div
                style={{
                  marginTop: "15px",
                  padding: "15px",
                  backgroundColor: "#f9f9f9",
                  borderRadius: "8px",
                }}
              >
                {showDesignFetchLayerLoading ? (
                  <div style={{ textAlign: "center", padding: "20px" }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "10px",
                      }}
                    >
                      <div
                        className="spinner-border spinner-border-sm"
                        role="status"
                        style={{ width: "16px", height: "16px" }}
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <span>
                        {language === "ar"
                          ? "جاري تحميل التصميمات..."
                          : "Loading Designs..."}
                      </span>
                    </div>
                  </div>
                ) : (
                  <ul
                    className="tf-filter-group d-flex gap-3 flex-wrap"
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(70px, 1fr))",
                      gap: "8px",
                    }}
                  >
                    {fetchedDesigns.map((d) => {
                      const selected = filters.design === String(d.id);
                      return (
                        <li key={`design-filter-${d.id}`}>
                          <input
                            type="radio"
                            name="design"
                            id={d.id}
                            checked={selected}
                            onChange={() =>
                              handleFilterSelect("design", String(d.id))
                            }
                            style={{ display: "none" }}
                          />
                          <label
                            htmlFor={d.id}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              padding: "6px 12px",
                              borderRadius: "20px",
                              border: selected
                                ? "2px solid #0e5aa7"
                                : "1px solid #e5e7eb",
                              background: selected ? "#0e5aa7" : "#fff",
                              color: selected ? "#fff" : "#000",
                              fontWeight: 600,
                              cursor: "pointer",
                              fontSize: "12px",
                              minHeight: "32px",
                              width: "100%",
                              justifyContent: "center",
                            }}
                          >
                            {d.label}
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
          </div>
        </section>
      )}
      {/* End Section Filter */}

      {/* Product Section - Only show when category is selected or search query exists */}
      {(selectedCategoryId || searchQuery) && (
        <section className="flat-spacing-2 shop-collection-section-6">
          <div className="container">
            <div
              className="tf-shop-control grid-3 align-items-center"
              style={{ display: "flex", alignItems: "center", gap: "20px" }}
            >
              <div className="tf-control-filter">
                {/* Filter button can be added here if needed */}
              </div>
              <ul
                className="tf-control-layout d-flex justify-content-center"
                style={{ flex: "1 1 auto", justifyContent: "center" }}
              >
                <li
                  className={`tf-view-layout-switch sw-layout-list list-layout${
                    layout === "list" ? " active" : ""
                  }`}
                  data-value-layout="list"
                  onClick={() => setLayout("list")}
                >
                  <div className="item">
                    <span className="icon icon-list"></span>
                  </div>
                </li>
                <li
                  className={`tf-view-layout-switch sw-layout-2${
                    layout === "grid-2" ? " active" : ""
                  }`}
                  data-value-layout="tf-col-2"
                  onClick={() => setLayout("grid-2")}
                >
                  <div className="item">
                    <span className="icon icon-grid-2"></span>
                  </div>
                </li>
                <li
                  className={`tf-view-layout-switch sw-layout-3${
                    layout === "grid-3" ? " active" : ""
                  }`}
                  data-value-layout="tf-col-3"
                  onClick={() => setLayout("grid-3")}
                >
                  <div className="item">
                    <span className="icon icon-grid-3"></span>
                  </div>
                </li>
                <li
                  className={`tf-view-layout-switch sw-layout-4${
                    layout === "grid-4" ? " active" : ""
                  }`}
                  data-value-layout="tf-col-4"
                  onClick={() => setLayout("grid-4")}
                >
                  <div className="item">
                    <span className="icon icon-grid-4"></span>
                  </div>
                </li>
                <li
                  className={`tf-view-layout-switch sw-layout-5${
                    layout === "grid-5" ? " active" : ""
                  }`}
                  data-value-layout="tf-col-5"
                  onClick={() => setLayout("grid-5")}
                >
                  <div className="item">
                    <span className="icon icon-grid-5"></span>
                  </div>
                </li>
                <li
                  className={`tf-view-layout-switch sw-layout-6${
                    layout === "grid-6" ? " active" : ""
                  }`}
                  data-value-layout="tf-col-6"
                  onClick={() => setLayout("grid-6")}
                >
                  <div className="item">
                    <span className="icon icon-grid-6"></span>
                  </div>
                </li>
              </ul>
              <div className="tf-control-sorting d-flex justify-content-end">
                <div className="tf-dropdown-sort">
                  <div
                    className="btn-select"
                    onClick={(e) => {
                      const menu = e.currentTarget.nextElementSibling;
                      if (menu) menu.classList.toggle("show");
                    }}
                  >
                    <span className="text-sort-value">
                      {language === "ar"
                        ? sortValue === "Sorting"
                          ? "الترتيب"
                          : sortValue === "Price, low to high"
                          ? "السعر: من الأقل إلى الأعلى"
                          : sortValue === "Price, high to low"
                          ? "السعر: من الأعلى إلى الأقل"
                          : sortValue
                        : sortValue}
                    </span>
                    <span className="icon icon-arrow-down"></span>
                  </div>
                  <div className="dropdown-menu">
                    {language === "ar"
                      ? [
                          "الترتيب",
                          "السعر: من الأقل إلى الأعلى",
                          "السعر: من الأعلى إلى الأقل",
                        ].map((opt, index) => {
                          const englishOpts = [
                            "Sorting",
                            "Price, low to high",
                            "Price, high to low",
                          ];
                          const optKey = englishOpts[index];
                          return (
                            <div
                              key={optKey}
                              className={`select-item${
                                sortValue === optKey ? " active" : ""
                              }`}
                              onClick={(e) => {
                                setSortValue(optKey);
                                const menu = e.currentTarget.parentElement;
                                if (menu) menu.classList.remove("show");
                              }}
                            >
                              <span className="text-value-item">{opt}</span>
                            </div>
                          );
                        })
                      : [
                          "Sorting",
                          "Price, low to high",
                          "Price, high to low",
                        ].map((opt) => (
                          <div
                            key={opt}
                            className={`select-item${
                              sortValue === opt ? " active" : ""
                            }`}
                            onClick={(e) => {
                              setSortValue(opt);
                              const menu = e.currentTarget.parentElement;
                              if (menu) menu.classList.remove("show");
                            }}
                          >
                            <span className="text-value-item">{opt}</span>
                          </div>
                        ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="wrapper-control-shop">
              <div className="meta-filter-shop">
                <div className="count-text" style={{ display: "none" }}>
                  {filteredProducts.length} products
                </div>
                <div className="count-text"></div>
                <div>
                  {appliedFilters.length > 0 && (
                    <div
                      id="applied-filters"
                      className="d-flex gap-10 flex-wrap"
                    >
                      {appliedFilters.map(([key, value]) => {
                        const filterKeyLabels = {
                          color: language === "ar" ? "اللون" : "Color",
                          size: language === "ar" ? "المقاس" : "Size",
                          rank: language === "ar" ? "التصنيف" : "Rank",
                          design: language === "ar" ? "التصميم" : "Design",
                          brand:
                            language === "ar" ? "العلامة التجارية" : "Brand",
                          availability:
                            language === "ar" ? "التوفر" : "Availability",
                        };
                        return (
                          <span
                            key={key}
                            className="badge border-line-black d-flex align-items-center gap-6"
                          >
                            {filterKeyLabels[key] ||
                              key.charAt(0).toUpperCase() + key.slice(1)}
                            : {getFilterLabel(key, value)}
                            <button
                              className="icon icon-close"
                              onClick={() => removeFilter(key)}
                              aria-label={
                                language === "ar"
                                  ? "إزالة المرشح"
                                  : "remove filter"
                              }
                            ></button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
                {appliedFilters.length > 0 && (
                  <button
                    id="remove-all"
                    className="remove-all-filters"
                    onClick={removeAllFilters}
                  >
                    {language === "ar" ? "إزالة الكل" : "Remove All"}{" "}
                    <i className="icon icon-close"></i>
                  </button>
                )}
              </div>

              {layout === "list" ? (
                <div className="tf-list-layout wrapper-shop" id="listLayout">
                  {isLoadingProducts ? (
                    <div className="text-center py-5">
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">
                          {language === "ar" ? "جاري التحميل..." : "Loading..."}
                        </span>
                      </div>
                      <p className="mt-3">
                        {language === "ar"
                          ? "جاري تحميل المنتجات..."
                          : "Loading products..."}
                      </p>
                    </div>
                  ) : isErrorProducts ? (
                    <div className="text-center py-5">
                      <p className="text-danger">
                        {language === "ar"
                          ? "فشل تحميل المنتجات"
                          : "Failed to load products"}
                      </p>
                    </div>
                  ) : (
                    filteredProducts.map((p) => (
                      <div
                        key={`product-${p.id}-${p.name}`}
                        className="card-product list-layout"
                        data-availability={p.availability}
                        data-brand={p.brand}
                      >
                        <div className="card-product-wrapper">
                          <Link
                            to={getProductLink(p)}
                            state={{ product: p }}
                            className="product-img"
                          >
                            <img
                              className="lazyload img-product"
                              data-src={
                                p.image ||
                                `https://erp-elsalab.odoo.com/web/image/${p.id}`
                              }
                              src={
                                p.image ||
                                `https://erp-elsalab.odoo.com/web/image/${p.id}`
                              }
                              alt={p.title}
                            />
                            <img
                              className="lazyload img-hover"
                              data-src={
                                p.image ||
                                `https://erp-elsalab.odoo.com/web/image/${p.id}`
                              }
                              src={
                                p.image ||
                                `https://erp-elsalab.odoo.com/web/image/${p.id}`
                              }
                              alt={p.title}
                            />
                            {p.list_price != null &&
                              p.price != null &&
                              Number(p.list_price) > 0 &&
                              Number(p.list_price) > Number(p.price) &&
                              Math.round(
                                ((Number(p.list_price) - Number(p.price)) /
                                  Number(p.list_price)) *
                                  100
                              ) > 0 && (
                                <div
                                  className="on-sale-wrap"
                                  style={{
                                    position: "absolute",
                                    top: 10,
                                    left: 10,
                                  }}
                                >
                                  <div
                                    className="on-sale-item"
                                    style={{
                                      backgroundColor: "var(--primary)",
                                      color: "#fff",
                                    }}
                                  >
                                    {Math.round(
                                      ((Number(p.list_price) -
                                        Number(p.price)) /
                                        Number(p.list_price)) *
                                        100
                                    )}
                                    % OFF
                                  </div>
                                </div>
                              )}
                          </Link>
                        </div>
                        <div className="card-product-info">
                          <Link
                            to={getProductLink(p)}
                            state={{ product: p }}
                            className="title link"
                          >
                            {p.title}
                          </Link>
                          {p.price != null && (
                            <div className="price-area">
                              <span
                                className="price current-price"
                                style={{ color: "var(--primary)" }}
                              >
                                EGP {Number(p.price).toFixed(1)}
                              </span>
                              {p.list_price != null &&
                                Number(p.list_price) > Number(p.price) && (
                                  <span
                                    className="price compare-at-price"
                                    style={{
                                      color: "#000",
                                      marginLeft: 8,
                                    }}
                                  >
                                    {Math.round(Number(p.list_price))}
                                  </span>
                                )}
                            </div>
                          )}
                          <button
                            className="tf-btn btn-fill animate-hover-btn rounded-0 mt-2"
                            onClick={() => {
                              addItem({ ...p, qty: 1 });
                              open();
                            }}
                          >
                            {language === "ar"
                              ? "أضف إلى السلة"
                              : "Add to Cart"}
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              ) : (
                <div
                  className={`tf-grid-layout wrapper-shop tf-col-${layout.replace(
                    "grid-",
                    ""
                  )}`}
                  id="gridLayout"
                >
                  {isLoadingProducts ? (
                    <div className="text-center py-5">
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">
                          {language === "ar" ? "جاري التحميل..." : "Loading..."}
                        </span>
                      </div>
                      <p className="mt-3">
                        {language === "ar"
                          ? "جاري تحميل المنتجات..."
                          : "Loading products..."}
                      </p>
                    </div>
                  ) : isErrorProducts ? (
                    <div className="text-center py-5">
                      <p className="text-danger">
                        {language === "ar"
                          ? "فشل تحميل المنتجات"
                          : "Failed to load products"}
                      </p>
                    </div>
                  ) : (
                    filteredProducts.map((p) => (
                      <div
                        key={`product-${p.id}-${p.name}`}
                        className="card-product grid"
                        data-availability={p.availability}
                        data-brand={p.brand}
                      >
                        <div className="card-product-wrapper">
                          <Link
                            to={getProductLink(p)}
                            state={{ product: p }}
                            className="product-img"
                          >
                            <img
                              className="lazyload img-product"
                              data-src={
                                p.image ||
                                `https://erp-elsalab.odoo.com/web/image/${p.id}`
                              }
                              src={
                                p.image ||
                                `https://erp-elsalab.odoo.com/web/image/${p.id}`
                              }
                              alt={p.title}
                            />
                            <img
                              className="lazyload img-hover"
                              data-src={
                                p.image ||
                                `https://erp-elsalab.odoo.com/web/image/${p.id}`
                              }
                              src={
                                p.image ||
                                `https://erp-elsalab.odoo.com/web/image/${p.id}`
                              }
                              alt={p.title}
                            />
                            {p.list_price != null &&
                              p.price != null &&
                              Number(p.list_price) > 0 &&
                              Number(p.list_price) > Number(p.price) &&
                              Math.round(
                                ((Number(p.list_price) - Number(p.price)) /
                                  Number(p.list_price)) *
                                  100
                              ) > 0 && (
                                <div
                                  className="on-sale-wrap"
                                  style={{
                                    position: "absolute",
                                    top: 10,
                                    left: 10,
                                  }}
                                >
                                  <div
                                    className="on-sale-item"
                                    style={{
                                      backgroundColor: "var(--primary)",
                                      color: "#fff",
                                    }}
                                  >
                                    {Math.round(
                                      ((Number(p.list_price) -
                                        Number(p.price)) /
                                        Number(p.list_price)) *
                                        100
                                    )}
                                    % OFF
                                  </div>
                                </div>
                              )}
                          </Link>
                          <div className="list-product-btn absolute-2">
                            <button
                              type="button"
                              className={`box-icon bg_white quick-add tf-btn-loading ${
                                cartItems.some(
                                  (item) => item.id === String(p.id)
                                )
                                  ? "product-in-cart"
                                  : ""
                              }`}
                              onClick={(e) => {
                                e.preventDefault();
                                addItem({ ...p, qty: 1 });
                                open();
                              }}
                            >
                              <span className="icon icon-bag"></span>
                              <span className="tooltip">
                                {language === "ar"
                                  ? "أضف إلى السلة"
                                  : "Add to cart"}
                              </span>
                            </button>
                            <button
                              type="button"
                              className={`box-icon bg_white wishlist btn-icon-action ${
                                p.is_favorite ? "active" : ""
                              }`}
                              onClick={async (e) => {
                                e.preventDefault();
                                try {
                                  const currentFavorite =
                                    p.is_favorite || false;
                                  const newFavoriteState = !currentFavorite;

                                  // Optimistically update local state
                                  setLocalFavoriteState((prev) => ({
                                    ...prev,
                                    [p.id]: newFavoriteState,
                                  }));

                                  await toggleFavoriteProduct(
                                    parseInt(p.id),
                                    newFavoriteState,
                                    null,
                                    p
                                  );

                                  console.log(
                                    `Product ${p.id} ${
                                      newFavoriteState
                                        ? "added to"
                                        : "removed from"
                                    } wishlist`
                                  );
                                } catch (error) {
                                  // Revert on error
                                  setLocalFavoriteState((prev) => ({
                                    ...prev,
                                    [p.id]: p.is_favorite || false,
                                  }));
                                  console.error(
                                    "Failed to toggle favorite:",
                                    error
                                  );
                                  alert(
                                    error.message ||
                                      (language === "ar"
                                        ? "فشل تحديث قائمة الأمنيات"
                                        : "Failed to update wishlist")
                                  );
                                }
                              }}
                            >
                              <span className="icon icon-heart"></span>
                              <span className="tooltip">
                                {p.is_favorite
                                  ? language === "ar"
                                    ? "إزالة من قائمة الأمنيات"
                                    : "Remove from Wishlist"
                                  : language === "ar"
                                  ? "أضف إلى قائمة الأمنيات"
                                  : "Add to Wishlist"}
                              </span>
                              <span className="icon icon-delete"></span>
                            </button>
                          </div>
                        </div>
                        <div className="card-product-info">
                          <Link
                            to={getProductLink(p)}
                            state={{ product: p }}
                            className="title link"
                          >
                            {p.title}
                          </Link>
                          {p.price != null && (
                            <div className="price-area">
                              <span
                                className="price current-price"
                                style={{ color: "var(--primary)" }}
                              >
                                EGP {Number(p.price).toFixed(1)}
                              </span>
                              {p.list_price != null &&
                                Number(p.list_price) > Number(p.price) && (
                                  <span
                                    className="price compare-at-price"
                                    style={{
                                      color: "#000",
                                      marginLeft: 8,
                                    }}
                                  >
                                    {Math.round(Number(p.list_price))}
                                  </span>
                                )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Removed duplicate static grid section */}

              {/* Pagination - show 5 pages at a time */}
              {(() => {
                // Calculate which 5 pages to show (e.g., if page=0-4 show 1-5, if page=5-9 show 6-10)
                const startPage = Math.floor(page / 5) * 5;
                const pagesToShow = [1, 2, 3, 4, 5].map((i) => startPage + i);
                const hasMore = productsResponse?.hasMore || false;
                const totalPages = productsResponse?.total && productsResponse.total > 0
                  ? Math.ceil(productsResponse.total / limit)
                  : null;

                return (
                  <ul className="wg-pagination tf-pagination-list justify-content-start" style={{ flexWrap: "wrap", gap: "8px", alignItems: "flex-start" }}>
                    {/* Previous button */}
                    <li className={page === 0 ? "disabled" : ""} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
                      <a
                        href="#"
                        className="pagination-link animate-hover-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          if (page === 0) return;

                          // Move one page back inside the current 5-page block.
                          // If we're at the first page in the block, jump to the last page of the previous block.
                          const blockStart = startPage;
                          let prevPage = page;

                          if (page > blockStart) {
                            prevPage = page - 1;
                          } else if (blockStart > 0) {
                            prevPage = blockStart - 1; // last page of previous block
                          }

                          if (prevPage < 0) return;

                          const next = new URLSearchParams(searchParams);
                          next.set("page", String(prevPage));
                          next.set("limit", String(limit));
                          setSearchParams(next);
                        }}
                      >
                        <span className="icon icon-arrow-left"></span>
                      </a>
                      <span style={{ fontSize: "0.75rem", whiteSpace: "nowrap" }}>
                        {language === "ar" ? "السابق" : "Previous"}
                      </span>
                    </li>

                    {/* Page numbers 1-5 (or current range) - always show all 5 pages in current range */}
                    {pagesToShow.map((pageNum) => {
                      const pageIndex = pageNum - 1; // Convert to 0-based
                      const isActive = pageIndex === page;

                      return (
                        <li key={pageNum} className={isActive ? "active" : ""}>
                          <a
                            href="#"
                            className="pagination-link animate-hover-btn"
                            onClick={(e) => {
                              e.preventDefault();
                              const next = new URLSearchParams(searchParams);
                              next.set("page", String(pageIndex));
                              next.set("limit", String(limit));
                              setSearchParams(next);
                            }}
                          >
                            {pageNum}
                          </a>
                        </li>
                      );
                    })}

                    {/* Next button: move one page forward until end of block, then jump to next 5-page block */}
                    <li style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
                      <a
                        href="#"
                        className="pagination-link animate-hover-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          // Ensure page is a valid number
                          const currentPage = Number(page) || 0;

                          // Determine the current 5-page block (0-based indexes)
                          // If currentPage = 0-4  -> block 0 (pages 0-4, shown as 1-5)
                          // If currentPage = 5-9  -> block 1 (pages 5-9, shown as 6-10)
                          const currentBlockStart = Math.floor(currentPage / 5) * 5;
                          const blockEndIndex = currentBlockStart + 4;

                          // Move one page forward
                          let nextPageIndex = currentPage + 1;

                          // If we've passed the end of this block, jump to the first page of the next block
                          if (nextPageIndex > blockEndIndex) {
                            nextPageIndex = currentBlockStart + 5;
                          }

                          const next = new URLSearchParams(searchParams);
                          next.set("page", String(nextPageIndex));
                          next.set("limit", String(limit));
                          setSearchParams(next);
                        }}
                        style={{
                          opacity: 1,
                          pointerEvents: "auto",
                          cursor: "pointer",
                        }}
                      >
                        <span className="icon icon-arrow-right"></span>
                      </a>
                      <span style={{ fontSize: "0.75rem", whiteSpace: "nowrap" }}>
                        {language === "ar" ? "التالي" : "Next"}
                      </span>
                    </li>
                  </ul>
                );
              })()}
            </div>
          </div>
        </section>
      )}
      {/* End Product Section */}

      <Footer />
    </div>
  );
}

export default ShopCollectionPage;
